import { Button, Col, Card, Select, Popover, message, Tag, Tooltip } from "antd";
import { FlagFilled } from "@ant-design/icons";
import React, { useCallback, useEffect, useRef, useState } from "react";

import ModalStep3 from "./ModalStep3";
import PolicyStatusCell from "./PolicyStatusCell";
import TreatmentPlanListRowInsuranceActions from "./TreatmentPlanListRowInsuranceActions";
import SelectPolicyItemModal from "./SelectPolicyItemModal";
import SikkaPolicyReviewModal from "../../InsuranceBreakDown/SikkaPolicyReviewModal";
import PolicyCompletionModal from "./shared/PolicyCompletionModal";
import { isPolicyComplete } from "../lib/policyCompleteness";
import { useEnsurePolicies } from "./useEnsurePolicies";
import SelectEstimateProvidersModal from "./shared/SelectEstimateProvidersModal";
import TagNotePopover from "./TagNotePopover";
import TreatmentCardScrapeJobIndicator from "../../ScrapeJobs/TreatmentCardScrapeJobIndicator";
import { armNextDispatchId, newDispatchId } from "../../../utils/dispatchTracking";
import { debugLog } from "../../../utils/debugLog";

import arrow from "../../../assets/icons/arrow-right.svg";
import phone from "../../../assets/icons/call.svg";
import email from "../../../assets/icons/sms3.svg";

import { controller } from "../controller";
import { controller as rootController } from "../../../controller";
import { AI_FETCH_METHODS, PREDETERMINATION_AI_METHODS, supportsAny, carrierCodeFromPolicy, portalForCarrier } from "../lib/carrierCapabilities";
import useRequestComposer from "../../InsuranceRequests/useRequestComposer";
import { triggerAIFetch, triggerEstimateFetch } from "../lib/aiFetch";
import { colorForTag, paletteForTag } from "../lib/tagColors";
import { MISSING_INFO_TAG_NAME } from "../../InsuranceRequests/requestTypes";
import "../style.css";

const { Meta } = Card;

/**
 * Clicks inside Ant Design portaled UI (Modal, Select dropdown, etc.) still bubble through the
 * React tree, so they can hit the patient Card's onClick and open plan details unintentionally.
 */
/** Keep in sync with `style.css` patient row `@media (max-width: …)` for `.card-elements-container`. */
const NARROW_TREATMENT_CARD_BREAKPOINT_PX = 1200;

function isEventFromPortaledOverlay(target) {
  if (!target || typeof target.closest !== "function") {
    return false;
  }
  return !!(
    target.closest(".ant-modal-wrap") ||
    target.closest(".ant-modal-root") ||
    target.closest(".ant-modal") ||
    target.closest(".ant-drawer") ||
    target.closest(".ant-select-dropdown") ||
    target.closest(".ant-picker-dropdown") ||
    target.closest(".ant-dropdown") ||
    target.closest(".ant-tooltip") ||
    target.closest(".ant-popover") ||
    target.closest(".ant-image-preview-wrap") ||
    target.closest(".ant-message") ||
    target.closest(".ant-notification")
  );
}

const BREAKDOWN_REQUESTED_TAG_NAME = "Breakdown - Requested";
const BREAKDOWN_REQUESTED_URGENT_TAG_NAME = "Breakdown - Requested - Urgent";
const ESTIMATE_REQUESTED_TAG_NAME = "Estimate - Requested";
const ESTIMATE_REQUESTED_URGENT_TAG_NAME = "Estimate - Requested - Urgent";

const TreatmentCard = ({ data, isTourDemo, setCurrentStep, setSelectedPatient, updateData, selectedSegment, allTags, onPatientInsuranceUpdated, onTagsCommitted }) => {
  // Only the guided-onboarding demo row carries data-onboarding-id anchors.
  const tourId = (id) => (isTourDemo ? id : undefined);
  const [item, setItem] = useState(data);
  // Deep-link: clicking a per-policy flag chip opens the insurance modal
  // pre-selected to THAT policy + the right tab. The nonce lets an identical
  // re-click retrigger the row-actions' open effect.
  const [deepLink, setDeepLink] = useState(null); // { policyId, tab, nonce }
  const deepLinkNonceRef = useRef(0);
  const openPolicyInModal = (policyId, tab) => {
    deepLinkNonceRef.current += 1;
    setDeepLink({ policyId, tab, nonce: deepLinkNonceRef.current });
  };
  const [showViewButton, setShowButton] = useState(window.innerWidth < NARROW_TREATMENT_CARD_BREAKPOINT_PX);
  const [isModalVisible1, setIsModalVisible1] = useState(false);
  const [isSettingTagForPatient, setIsSettingTagForPatient] = useState(false);
  const [isRequestingBreakdown, setIsRequestingBreakdown] = useState(false);
  const [isRequestingBreakdownUrgent, setIsRequestingBreakdownUrgent] = useState(false);
  const [isAIFetching, setIsAIFetching] = useState(false);
  // Requests from this row go through the Insurance Verification composer, so a
  // request row exists before any agent runs. The composer also does the policy
  // top-up, selection and completeness review these buttons used to do by hand.
  const { openComposer, composer } = useRequestComposer();

  function openRequestComposer(e, requestType, closePopover) {
    if (e && e.stopPropagation) e.stopPropagation();
    if (closePopover) closePopover(false);
    openComposer(data, { requestType: requestType });
  }

  const [popoverOpen, setPopoverOpen] = useState(false);
  // Controlled open state for the tag dropdown so that adding a tag can close
  // it and immediately surface the note popover (instead of waiting for the
  // user to click somewhere else to close the dropdown first).
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [selectedPatientTags, setSelectedPatientTags] = useState(allTags.filter((tag) => data.tags.includes(tag.name)).map((tag) => tag.id));
  // `committedPatientTags` mirrors what's actually saved on the backend so
  // we can diff the in-flight `selectedPatientTags` against it when the
  // dropdown closes — that's how we detect "the user just added a tag"
  // and pop the note popover instead of silently committing.
  const [committedPatientTags, setCommittedPatientTags] = useState(allTags.filter((tag) => data.tags.includes(tag.name)).map((tag) => tag.id));
  // Mode of the inline tag-note popover anchored to the Select:
  //   null     — popover closed
  //   "add"    — a new tag was selected; collect/update the patient note
  //   "remove" — a tag is being removed and the patient already has a
  //              note; allow staff to review/edit before the tag drops
  const [tagNotePopoverMode, setTagNotePopoverMode] = useState(null);
  // Stashed payload for the remove path: the tag list we'd send if the
  // user confirms the removal. Set in the deselect handler, consumed
  // when the popover commits.
  const [pendingRemoveTags, setPendingRemoveTags] = useState(null);
  const [capabilityMatrix, setCapabilityMatrix] = useState(null);
  const [patientPolicies, setPatientPolicies] = useState([]);
  // When the patient has more than one policy, the Instant Check button
  // opens this modal so the user can pick which policy the AI agent
  // should fetch — mirrors the experience inside the breakdown modal.
  const [showSelectPolicyModal, setShowSelectPolicyModal] = useState(false);
  const [aiFetchSelectedPolicy, setAiFetchSelectedPolicy] = useState(undefined);
  // Estimate (predetermination) row-action state — mirrors the breakdown trio
  // above. Kept independent so the existing breakdown flow is untouched.
  const [isRequestingEstimate, setIsRequestingEstimate] = useState(false);
  const [isRequestingEstimateUrgent, setIsRequestingEstimateUrgent] = useState(false);
  const [isEstimateFetching, setIsEstimateFetching] = useState(false);
  const [estimatePopoverOpen, setEstimatePopoverOpen] = useState(false);
  const [showSelectEstimatePolicyModal, setShowSelectEstimatePolicyModal] = useState(false);
  const [estimateFetchSelectedPolicy, setEstimateFetchSelectedPolicy] = useState(undefined);
  // Provider-account picker for the estimate fetch (offices with >1 account).
  const [showEstimateProviderPicker, setShowEstimateProviderPicker] = useState(false);
  const [estimateAccounts, setEstimateAccounts] = useState([]);
  const [estimatePickerPolicy, setEstimatePickerPolicy] = useState(null);
  const [estimatePickerBusy, setEstimatePickerBusy] = useState(false);

  const isARSegment = selectedSegment === "ar-handled" || selectedSegment === "ar-cases";
  const showDaysColumn = ["cared-for", "past-appointment", "requiring-action"].includes(selectedSegment);
  const showTagSelector = !isARSegment;
  // On the New Breakdowns / New Estimates tabs the row collapses its three
  // action buttons into a single "View Breakdown" / "View Estimate" button.
  const isFetchedItemSegment = selectedSegment === "fresh-breakdowns" || selectedSegment === "new-estimates";

  // The row Tags dropdown handles ONLY the office's CUSTOM tags — the system
  // Requested/Outcome tags are driven by the Request/Fetch buttons + the scrape
  // flow, so they're excluded here to keep the dropdown simple and predictable.
  const isDefaultTagId = (id) => {
    const t = allTags.find((x) => x.id === id);
    return !!(t && t.is_default_tag);
  };
  const tagDropdownOptions = allTags
    .filter((tag) => !tag.is_default_tag)
    .map((tag) => ({ label: tag.name, value: tag.id }));
  const customSelectedTagIds = selectedPatientTags.filter((id) => !isDefaultTagId(id));
  const handleCustomTagsChange = (nextCustomIds) => {
    // setTagsForPatient is a full-replace PATCH → re-include the patient's
    // current SYSTEM tags so editing a custom tag can never wipe the
    // Requested/Verified/Needs-Attention tags. Commit directly (no note popover).
    const systemIds = selectedPatientTags.filter(isDefaultTagId);
    const merged = [...systemIds, ...nextCustomIds];
    setSelectedPatientTags(merged);
    commitTagsToBackend(merged);
  };

  const breakdownRequestedTag = allTags.find((tag) => tag.name === BREAKDOWN_REQUESTED_TAG_NAME);
  const hasBreakdownRequested = breakdownRequestedTag && selectedPatientTags.includes(breakdownRequestedTag.id);
  const breakdownRequestedUrgentTag = allTags.find((tag) => tag.name === BREAKDOWN_REQUESTED_URGENT_TAG_NAME);
  const hasBreakdownRequestedUrgent = breakdownRequestedUrgentTag && selectedPatientTags.includes(breakdownRequestedUrgentTag.id);
  const estimateRequestedTag = allTags.find((tag) => tag.name === ESTIMATE_REQUESTED_TAG_NAME);
  const hasEstimateRequested = estimateRequestedTag && selectedPatientTags.includes(estimateRequestedTag.id);
  const estimateRequestedUrgentTag = allTags.find((tag) => tag.name === ESTIMATE_REQUESTED_URGENT_TAG_NAME);
  const hasEstimateRequestedUrgent = estimateRequestedUrgentTag && selectedPatientTags.includes(estimateRequestedUrgentTag.id);

  useEffect(() => {
    setItem(data);
  }, [data]);

  // When the parent's data refreshes (e.g., after onPatientInsuranceUpdated
  // re-fetches the list), sync the local selected/committed tag state to
  // match the freshly-fetched tags — but only if the user has no pending
  // uncommitted change in the dropdown. This prevents a background refresh
  // from clobbering an in-progress edit, while still pulling in changes
  // applied elsewhere (or by another browser tab).
  useEffect(() => {
    const freshIds = allTags
      .filter((tag) => data.tags && data.tags.includes(tag.name))
      .map((tag) => tag.id);
    const freshKey = [...freshIds].sort().join(",");
    const committedKey = [...committedPatientTags].sort().join(",");
    const selectedKey = [...selectedPatientTags].sort().join(",");
    if (freshKey !== committedKey && committedKey === selectedKey) {
      setSelectedPatientTags(freshIds);
      setCommittedPatientTags(freshIds);
    }
    // We intentionally key only on `data.tags` (the parent's authoritative
    // tag list) — we don't want this effect to re-run from local commits
    // since those already update committedPatientTags directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.tags && data.tags.join(",")]);

  // Handle displaying view button
  useEffect(function () {
    const handleResize = () => setShowButton(window.innerWidth < NARROW_TREATMENT_CARD_BREAKPOINT_PX);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load carrier capability matrix (shared cache; one fetch per session).
  useEffect(() => {
    let cancelled = false;
    rootController
      .getCarrierCapabilities()
      .then((matrix) => {
        if (!cancelled) setCapabilityMatrix(matrix);
      })
      .catch((err) => console.error("Failed to load carrier capabilities", err));
    return () => {
      cancelled = true;
    };
  }, []);

  // Load patient's insurance policies so we know the primary carrier.
  const refreshCardPolicies = useCallback(
    async function refreshCardPoliciesInner() {
      if (!data || !data.id) return [];
      try {
        const res = await rootController.getPatientPolicies(data.id);
        if (res && res.status < 250 && Array.isArray(res.json)) {
          setPatientPolicies(res.json);
          return res.json;
        }
      } catch (err) {
        console.error("Failed to load patient policies", err);
      }
      return [];
    },
    [data && data.id],
  );

  useEffect(() => {
    refreshCardPolicies();
  }, [refreshCardPolicies]);

  // Auto-fetch PMS policies when this patient has none yet (shared hook with the
  // insurance modal). Confident carriers save silently; an unknown carrier opens
  // the review modal via ensurePoliciesReviewProps. data.office may be absent on
  // some rows — the backend derives the office from the patient regardless.
  const { ensurePolicies, refreshFromPms, reviewProps: ensurePoliciesReviewProps } = useEnsurePolicies(
    data && data.id,
    data && data.office && data.office.id,
    refreshCardPolicies,
  );

  const primaryPolicy =
    (patientPolicies || []).find((p) => p.insurance_type === "primary") ||
    (patientPolicies && patientPolicies[0]) ||
    null;
  const aiBreakdownAvailable = !!(
    capabilityMatrix &&
    primaryPolicy &&
    supportsAny(capabilityMatrix, carrierCodeFromPolicy(primaryPolicy), "breakdown", AI_FETCH_METHODS)
  );
  const aiEstimateAvailable = !!(
    capabilityMatrix &&
    primaryPolicy &&
    supportsAny(capabilityMatrix, carrierCodeFromPolicy(primaryPolicy), "predetermination", PREDETERMINATION_AI_METHODS)
  );

  // function handleShowViewButton() {
  //   if (window.location.href.includes("treatment-plans")) {
  //     return window.innerWidth < 1600;
  //   } else if (window.location.href.includes("treatment-plans-account-receivable")) {
  //     console.log("2");
  //     return window.innerWidth > 1660;
  //   }
  // }

  const handleCardClick = (e) => {
    // Prevent click when interacting with Select on the card (tags)
    if (e.target.closest(".ant-select")) return;

    if (isEventFromPortaledOverlay(e.target)) return;

    setCurrentStep(2);
    // Use `item` (local state) rather than `data` (the original prop) so any
    // optimistic in-list mutations — e.g. the manual attention note just
    // added via the popup — propagate into the detail page without waiting
    // for the parent to refetch the patient list.
    setSelectedPatient(item);
    localStorage.setItem("patient_id", item.id);
    localStorage.removeItem("modalSelectId");
    localStorage.removeItem("patientName");
    localStorage.removeItem("newPatientName");
  };

  // Single commit path used by every save flow (dropdown close, deselect,
  // add-note popover Done, remove-note popover Confirm). Pass
  // `manualAttentionNote` whenever the user just went through the note
  // popover — the backend then sets/clears the patient's shared note to
  // exactly that value. Pass `undefined` to leave the existing note alone.
  const commitTagsToBackend = async (nextTags, manualAttentionNote) => {
    try {
      setIsSettingTagForPatient(true);
      const res = await controller.setTagsForPatient(data.id, nextTags, manualAttentionNote);
      if (res && res.status < 300) {
        setCommittedPatientTags(nextTags);
        // Optimistically mirror the new state onto `item` so anything
        // sourced from `item` is immediately consistent without waiting
        // for the parent's refetch — this is what `setSelectedPatient(item)`
        // hands to the treatment-plan detail page (banner) and what flows
        // into the breakdown modal's failure card.
        const nextTagNames = allTags
          .filter((tag) => nextTags.includes(tag.id))
          .map((tag) => tag.name);
        setItem((prev) => {
          const next = { ...prev, tags: nextTagNames };
          // Only touch the note locally when this request carried a note —
          // mirrors the backend's "respect manual_attention_note if present,
          // leave it alone otherwise" behavior.
          if (manualAttentionNote !== undefined) {
            const trimmed = (manualAttentionNote || "").trim();
            next.manual_attention_note = trimmed || null;
            next.manual_attention_note_set_at = trimmed ? new Date().toISOString() : null;
            if (!trimmed) next.manual_attention_note_set_by = null;
          }
          return next;
        });
        // Refresh the parent list so a later remount (e.g. returning from
        // the detail page) initializes from authoritative data instead of
        // a stale pre-commit snapshot. We use the *silent* variant
        // (`onTagsCommitted`) so the parent's loading flag doesn't flip —
        // the loud variant (`onPatientInsuranceUpdated`) replaces the
        // entire list block with a spinner mid-commit, which unmounts
        // every TreatmentCard and would tear down this very component
        // while its own `finally` is still running.
        if (typeof onTagsCommitted === "function") {
          onTagsCommitted();
        } else if (typeof onPatientInsuranceUpdated === "function") {
          // Backwards compat fallback for callers that haven't been
          // updated to pass the silent prop yet.
          onPatientInsuranceUpdated();
        }
      } else {
        message.error("Something went wrong while setting tags");
        setSelectedPatientTags(committedPatientTags); // rollback local UI
      }
    } catch (err) {
      message.error("Something went wrong while setting tags");
      console.error(err);
      setSelectedPatientTags(committedPatientTags);
    } finally {
      setIsSettingTagForPatient(false);
    }
  };

  // Dropdown closed. If the user just added any new tag(s), pop the note
  // popover anchored to the Select so they can capture/edit the patient
  // note in the same gesture; otherwise commit the current selection.
  // Skips entirely when nothing changed.
  const handleDropdownClose = () => {
    const addedAnyTag = selectedPatientTags.some((id) => !committedPatientTags.includes(id));

    if (addedAnyTag) {
      setTagNotePopoverMode("add");
      return;
    }

    // Nothing structurally changed — skip the network call. Compare as sets
    // by length-then-sorted-equality to handle re-ordered selections.
    if (selectedPatientTags.length === committedPatientTags.length) {
      const a = [...selectedPatientTags].sort().join(",");
      const b = [...committedPatientTags].sort().join(",");
      if (a === b) return;
    }
    commitTagsToBackend(selectedPatientTags);
  };

  // Intercept the X-on-chip and dropdown-uncheck paths. Removing a tag from
  // a patient that already has a manual note pops the remove popover so
  // staff can review/edit the note in the same gesture before the tag drops.
  // Patients without a note skip the popover and untag silently.
  const handleDeselect = (id) => {
    // `selectedPatientTags` at this point still reflects the pre-onChange
    // state in this closure (onChange fired first but its setState hasn't
    // re-rendered yet) — same situation the original handler relied on, so
    // we compute the next list explicitly off of it.
    const next = selectedPatientTags.filter((tag) => tag !== id);
    if (item.manual_attention_note) {
      if (next.length === 0) {
        // Removing the last tag — the note has nothing left to be "about".
        // Auto-clear in the same commit instead of popping the editor;
        // otherwise the patient ends up untagged but with a lingering
        // attention flag, which reads as broken.
        commitTagsToBackend(next, "");
        return;
      }
      setPendingRemoveTags(next);
      setTagNotePopoverMode("remove");
      return;
    }
    commitTagsToBackend(next);
  };

  // User clicked Save on the add-note popover. Commit with whatever note
  // they typed (empty string is an explicit clear; the backend normalizes).
  const handleAddNoteConfirm = (note) => {
    setTagNotePopoverMode(null);
    commitTagsToBackend(selectedPatientTags, note);
  };

  // User dismissed the add-note popover. Roll the local selection back to
  // the committed state so the just-added chip(s) disappear from the row.
  const handleAddNoteCancel = () => {
    setSelectedPatientTags(committedPatientTags);
    setTagNotePopoverMode(null);
  };

  // User confirmed the tag removal. Commit the stashed `pendingRemoveTags`
  // with the (possibly edited) note from the popover.
  const handleRemoveNoteConfirm = (note) => {
    setTagNotePopoverMode(null);
    if (pendingRemoveTags) {
      commitTagsToBackend(pendingRemoveTags, note);
      setPendingRemoveTags(null);
    }
  };

  // User dismissed the remove popover. Put the chip back so the UI reverts
  // to the saved state.
  const handleRemoveNoteCancel = () => {
    setSelectedPatientTags(committedPatientTags);
    setTagNotePopoverMode(null);
    setPendingRemoveTags(null);
  };

  const handleRequestBreakdown = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!breakdownRequestedTag) {
      message.error(`The "${BREAKDOWN_REQUESTED_TAG_NAME}" tag is not set up for this office.`);
      return;
    }
    if (hasBreakdownRequested) {
      message.info("Breakdown already requested for this patient.");
      return;
    }
    // Fetch policies from the PMS first if the patient has none, so the request
    // attaches to a real policy instead of landing as a policy-less orphan tag.
    await ensurePolicies(patientPolicies);
    const nextTags = [...selectedPatientTags, breakdownRequestedTag.id];
    try {
      setIsRequestingBreakdown(true);
      await controller.setTagsForPatient(data.id, nextTags);
      setSelectedPatientTags(nextTags);
      message.success("Breakdown requested.");
    } catch (err) {
      message.error("Failed to request breakdown. Please try again.");
      console.error(err);
    } finally {
      setIsRequestingBreakdown(false);
    }
  };

  const handleRequestBreakdownUrgent = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!breakdownRequestedUrgentTag) {
      message.error(`The "${BREAKDOWN_REQUESTED_URGENT_TAG_NAME}" tag is not set up for this office.`);
      return;
    }
    if (hasBreakdownRequestedUrgent) {
      message.info("Urgent breakdown already requested for this patient.");
      return;
    }
    // Fetch policies from the PMS first if the patient has none (orphan-tag fix).
    await ensurePolicies(patientPolicies);
    const nextTags = [...selectedPatientTags, breakdownRequestedUrgentTag.id];
    try {
      setIsRequestingBreakdownUrgent(true);
      await controller.setTagsForPatient(data.id, nextTags);
      setSelectedPatientTags(nextTags);
      message.success("Urgent breakdown requested.");
    } catch (err) {
      message.error("Failed to request urgent breakdown. Please try again.");
      console.error(err);
    } finally {
      setIsRequestingBreakdownUrgent(false);
    }
  };

  // Dispatch the AI fetch for a specific policy. Pulled out of handleFetchByAI
  // so it can be reused both for the single-policy fast path and as the
  // callback the SelectPolicyItemModal invokes once the user has picked.
  const triggerAIFetchForPolicy = async (policy) => {
    if (!policy || !policy.id) {
      message.error("This patient has no insurance policy to fetch for.");
      return;
    }
    // Mint a dispatch_id and arm it so the next outgoing POST carries the
    // X-Dispatch-Id header. The backend will log the same id on view_entry,
    // letting us join the FE click to the BE pipeline in scrape_flow.log.
    const dispatchId = newDispatchId();
    armNextDispatchId(dispatchId);
    debugLog.info("button", "scrape_button_click", {
      dispatch_id: dispatchId,
      patient_id: data && data.id,
      policy_id: policy.id,
      reason: "ai_fetch_card_action",
    });
    try {
      setIsAIFetching(true);
      const res = await triggerAIFetch(policy.id, {});
      debugLog.info("button", "scrape_button_dispatch_response", {
        dispatch_id: dispatchId,
        http_status: res && res.ok ? 200 : 0,
        reason: res && res.ok ? "ok" : (res && res.error) || "unknown",
      });
      if (res.ok) {
        message.success("AI fetch started. Check the Insurance Breakdown modal for results.");
      } else {
        message.error(res.error || "Failed to start the AI fetch.");
      }
    } finally {
      setIsAIFetching(false);
    }
  };

  const [completionPolicy, setCompletionPolicy] = useState(undefined);
  // Stored as a function-returning-function so setState doesn't call it.
  const [resumeDispatch, setResumeDispatch] = useState(undefined);

  // Single-policy Instant AI Fetch gate. Tops the policy up from the PMS, then
  // returns it when it has everything the scrape needs. When it doesn't, opens
  // the completion modal and stashes `resume` so the dispatch continues once the
  // user fills the gaps — returns undefined so the caller stops here.
  const policyReadyForDispatch = async (policy, resume) => {
    const refreshed = await refreshFromPms();
    const current =
      (refreshed || []).find(function (p) { return p.id === policy.id; }) || policy;
    if (isPolicyComplete(current)) return current;
    setCompletionPolicy(current);
    setResumeDispatch(function () { return resume; });
    return undefined;
  };

  const handleFetchByAI = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setPopoverOpen(false);
    // No policies yet? Auto-fetch from the PMS first, then continue as usual.
    const policies = await ensurePolicies(patientPolicies);
    if (policies.length === 0) {
      message.error("This patient has no insurance policy to fetch for.");
      return;
    }
    // Single-policy fast path: nothing to disambiguate, but still top up from
    // the PMS and check the data the scrape needs — a missing member ID /
    // contract # / member DOB is what makes the carrier answer "we couldn't
    // find the claimant" minutes later.
    if (policies.length === 1) {
      const current = await policyReadyForDispatch(policies[0], function (p) {
        triggerAIFetchForPolicy(p);
      });
      if (current) await triggerAIFetchForPolicy(current);
      return;
    }
    // Multiple policies: surface the selection modal so the user picks which
    // policy this run is for. Mirrors handleScraperAgentCall in
    // TreatmentPlanListRowInsuranceActions.js.
    setAiFetchSelectedPolicy(undefined);
    setShowSelectPolicyModal(true);
  };

  // Adapter that the modal calls when the user clicks "Fetch". The modal's
  // existing API is `checkPatientInsuranceEligibility(agentType, policyId)`;
  // we ignore agentType (always "breakdown" here) and route the picked policy
  // through our own AI-fetch trigger.
  const onPolicyChosenForAIFetch = (_agentType, policyId) => {
    const policy = (patientPolicies || []).find((p) => p.id === policyId);
    if (!policy) {
      message.error("Selected policy could not be found.");
      return;
    }
    triggerAIFetchForPolicy(policy);
  };

  // --- Estimate (predetermination) row actions — mirror the breakdown ones ---

  const handleRequestEstimate = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!estimateRequestedTag) {
      message.error(`The "${ESTIMATE_REQUESTED_TAG_NAME}" tag is not set up for this office.`);
      return;
    }
    if (hasEstimateRequested) {
      message.info("Estimate already requested for this patient.");
      return;
    }
    // Fetch policies from the PMS first if the patient has none (orphan-tag fix).
    await ensurePolicies(patientPolicies);
    const nextTags = [...selectedPatientTags, estimateRequestedTag.id];
    try {
      setIsRequestingEstimate(true);
      await controller.setTagsForPatient(data.id, nextTags);
      setSelectedPatientTags(nextTags);
      message.success("Estimate requested.");
    } catch (err) {
      message.error("Failed to request estimate. Please try again.");
      console.error(err);
    } finally {
      setIsRequestingEstimate(false);
    }
  };

  const handleRequestEstimateUrgent = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!estimateRequestedUrgentTag) {
      message.error(`The "${ESTIMATE_REQUESTED_URGENT_TAG_NAME}" tag is not set up for this office.`);
      return;
    }
    if (hasEstimateRequestedUrgent) {
      message.info("Urgent estimate already requested for this patient.");
      return;
    }
    // Fetch policies from the PMS first if the patient has none (orphan-tag fix).
    await ensurePolicies(patientPolicies);
    const nextTags = [...selectedPatientTags, estimateRequestedUrgentTag.id];
    try {
      setIsRequestingEstimateUrgent(true);
      await controller.setTagsForPatient(data.id, nextTags);
      setSelectedPatientTags(nextTags);
      message.success("Urgent estimate requested.");
    } catch (err) {
      message.error("Failed to request urgent estimate. Please try again.");
      console.error(err);
    } finally {
      setIsRequestingEstimateUrgent(false);
    }
  };

  // Fire the estimate (predetermination) AI fetch for a specific policy under
  // the chosen provider accounts. Fire-and-forget: 2FA / polling / results
  // surface in the Insurance Estimate modal, same as the breakdown Instant Check.
  const triggerEstimateFetchForPolicy = async (policy, providerIds) => {
    if (!policy || !policy.id) {
      message.error("This patient has no insurance policy to fetch for.");
      return;
    }
    const dispatchId = newDispatchId();
    armNextDispatchId(dispatchId);
    debugLog.info("button", "scrape_button_click", {
      dispatch_id: dispatchId,
      patient_id: data && data.id,
      policy_id: policy.id,
      reason: "ai_estimate_fetch_card_action",
    });
    try {
      setIsEstimateFetching(true);
      const res = await triggerEstimateFetch(policy.id, providerIds || []);
      debugLog.info("button", "scrape_button_dispatch_response", {
        dispatch_id: dispatchId,
        http_status: res && res.ok ? 200 : 0,
        reason: res && res.ok ? "ok" : (res && res.error) || "unknown",
      });
      if (res.ok) {
        message.success("Estimate fetch started. Check the Insurance Estimate modal for results.");
      } else {
        message.error(res.error || "Failed to start the estimate fetch.");
      }
    } finally {
      setIsEstimateFetching(false);
    }
  };

  // Resolve which provider account(s) to fetch the estimate under, then
  // dispatch. Mirrors the Estimate modal's handleAIVerification: a
  // predetermination only appears under the submitting provider's account, so
  // when the office has >1 account for the carrier's portal we prompt; 0/1
  // accounts dispatch directly (backend uses the office default for 0).
  const resolveEstimateProvidersThenDispatch = async (policy) => {
    if (!policy || !policy.id) {
      message.error("This patient has no insurance policy to fetch for.");
      return;
    }
    const portal = portalForCarrier(carrierCodeFromPolicy(policy));
    const officeId =
      typeof window !== "undefined" && window.localStorage
        ? window.localStorage.getItem("selectedOffice")
        : null;
    if (!portal || !officeId) {
      await triggerEstimateFetchForPolicy(policy, []);
      return;
    }
    try {
      setEstimatePickerBusy(true);
      const res = await rootController.listPortalCredentials(officeId);
      const all = (res && res.credentials) || [];
      const accounts = all.filter((c) => c.portal_code === portal && c.provider);
      if (accounts.length > 1) {
        setEstimateAccounts(accounts);
        setEstimatePickerPolicy(policy);
        setShowEstimateProviderPicker(true);
        return;
      }
      const ids = accounts.length === 1 ? [accounts[0].provider.id] : [];
      await triggerEstimateFetchForPolicy(policy, ids);
    } catch (err) {
      // Listing accounts is best-effort — fall back to the default account.
      await triggerEstimateFetchForPolicy(policy, []);
    } finally {
      setEstimatePickerBusy(false);
    }
  };

  const handleFetchEstimateByAI = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setEstimatePopoverOpen(false);
    // No policies yet? Auto-fetch from the PMS first, then continue as usual.
    const policies = await ensurePolicies(patientPolicies);
    if (policies.length === 0) {
      message.error("This patient has no insurance policy to fetch for.");
      return;
    }
    if (policies.length === 1) {
      const current = await policyReadyForDispatch(policies[0], function (p) {
        resolveEstimateProvidersThenDispatch(p);
      });
      if (current) await resolveEstimateProvidersThenDispatch(current);
      return;
    }
    // Multiple policies: pick which policy this estimate run is for first.
    setEstimateFetchSelectedPolicy(undefined);
    setShowSelectEstimatePolicyModal(true);
  };

  // Adapter the estimate policy-picker modal calls once a policy is chosen.
  const onPolicyChosenForEstimateFetch = (_agentType, policyId) => {
    const policy = (patientPolicies || []).find((p) => p.id === policyId);
    if (!policy) {
      message.error("Selected policy could not be found.");
      return;
    }
    resolveEstimateProvidersThenDispatch(policy);
  };

  return (
    <>
      {/* The shared request composer. Rendered at the card root, NOT inside the
          view-button branch: that branch is conditional, so the drawer silently
          never mounted for rows without it. */}
      {composer}
      <ModalStep3 isModalVisible={isModalVisible1} setIsModalVisible={setIsModalVisible1} selectedMember={item} />

      {/* Policy-picker modal that opens when the user clicks "Instant Check" on
       * a multi-policy patient. The modal greys out policies whose carrier
       * doesn't support an AI breakdown fetch (e.g., a tertiary policy on a
       * carrier we haven't built a scraper for) so the user can't pick a
       * dead-end policy. */}
      <SelectPolicyItemModal
        showSelectPolicyItemModal={showSelectPolicyModal}
        setShowSelectPolicyItemModal={setShowSelectPolicyModal}
        patientPolicies={patientPolicies || []}
        selectedPolicy={aiFetchSelectedPolicy}
        setSelectedPolicy={setAiFetchSelectedPolicy}
        agentType={"breakdown"}
        setAgentType={() => {}}
        patientID={item && item.id}
        checkPatientInsuranceEligibility={onPolicyChosenForAIFetch}
        setShowPatientInsuranceFormModal={() => {}}
        onPoliciesChanged={() => {}}
      />

      {/* Estimate (predetermination) policy-picker — opens on "Instant Estimate"
       * for a multi-policy patient; greys out policies whose carrier can't do a
       * predetermination fetch. */}
      <SelectPolicyItemModal
        showSelectPolicyItemModal={showSelectEstimatePolicyModal}
        setShowSelectPolicyItemModal={setShowSelectEstimatePolicyModal}
        patientPolicies={patientPolicies || []}
        selectedPolicy={estimateFetchSelectedPolicy}
        setSelectedPolicy={setEstimateFetchSelectedPolicy}
        agentType={"predetermination"}
        setAgentType={() => {}}
        patientID={item && item.id}
        checkPatientInsuranceEligibility={onPolicyChosenForEstimateFetch}
        setShowPatientInsuranceFormModal={() => {}}
        onPoliciesChanged={() => {}}
      />

      {/* Provider-account picker for the estimate fetch — shown only when the
       * office has >1 account for the carrier's portal (one scrape per account
       * chosen). */}
      <SelectEstimateProvidersModal
        open={showEstimateProviderPicker}
        onCancel={() => setShowEstimateProviderPicker(false)}
        onConfirm={(providerIds) => {
          setShowEstimateProviderPicker(false);
          triggerEstimateFetchForPolicy(estimatePickerPolicy, providerIds);
        }}
        accounts={estimateAccounts}
        defaultSelectedIds={item && item.provider_id ? [item.provider_id] : []}
        confirmLoading={estimatePickerBusy}
      />

      {/* Review modal for auto-fetched policies whose PMS carrier couldn't be
          confidently matched ("review only unknowns"). */}
      <PolicyCompletionModal
        open={!!completionPolicy}
        policy={completionPolicy}
        patientId={data && data.id}
        confirmLabel="Fetch"
        onCancel={function () {
          setCompletionPolicy(undefined);
          setResumeDispatch(undefined);
        }}
        onCompleted={async function () {
          const resume = resumeDispatch;
          const policyId = completionPolicy && completionPolicy.id;
          setCompletionPolicy(undefined);
          setResumeDispatch(undefined);
          const refreshed = await refreshCardPolicies();
          const current = (refreshed || []).find(function (p) { return p.id === policyId; });
          if (resume && current) resume(current);
        }}
      />

      {ensurePoliciesReviewProps && <SikkaPolicyReviewModal {...ensurePoliciesReviewProps} />}

      <Col span={24} data-onboarding-id={tourId("patient-row-demo")}>
        <Card className="treatment-patient-card" bodyStyle={{ padding: "14px 24px" }} style={{ borderRadius: "8px", margin: "16px 0 8px" }}>
          <div className="card-elements-container">
            {/* Patient Name */}
            <div
              className={`name-and-view-button ${showDaysColumn || window.location.href.includes("treatment-plans-account-receivable") ? "grid-layout" : "not-grid-layout"}`}
            >
              <Tooltip title="View treatment plan">
                <div
                  className="name-col"
                  onClick={handleCardClick}
                  style={{ cursor: "pointer" }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleCardClick(e);
                    }
                  }}
                >
                  <Meta title={`${item.first_name} ${item.last_name}`} style={{ margin: 0 }} />
                  {/* Phone + email tucked under the name (small) so the row
                      stays compact while freeing the middle for the note. */}
                  <div className="treatment-card-contacts">
                    <Tooltip title={item.phone || item.phone_number || undefined}>
                      <div className="treatment-card-contact-line">
                        <img src={phone} alt="" className="treatment-card-contact-icon-sm" />
                        <span className="treatment-card-contact-text">{item.phone || item.phone_number || "-"}</span>
                      </div>
                    </Tooltip>
                    <Tooltip title={item.email || undefined}>
                      <div className="treatment-card-contact-line">
                        <img src={email} alt="" className="treatment-card-contact-icon-sm" />
                        <span className="treatment-card-contact-text">{item.email || "-"}</span>
                      </div>
                    </Tooltip>
                  </div>
                </div>
              </Tooltip>

              {showViewButton && (
                <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                  {!showTagSelector && (
                    <TreatmentCardScrapeJobIndicator
                      patientId={item.id}
                      patientName={`${item.first_name} ${item.last_name}`}
                    />
                  )}
                  <TreatmentPlanListRowInsuranceActions patient={item} isTourDemo={isTourDemo} selectedSegment={selectedSegment} onPatientInsuranceUpdated={onPatientInsuranceUpdated} deepLinkRequest={deepLink} />
                  <ViewButton handleCardClick={handleCardClick} />
                </div>
              )}
            </div>

            {/* Main Content Wrapper - takes available space */}
            <div
              className={`no-view-button-container ${showDaysColumn ? "show-days-column" : "not-show-days-column"} ${window.location.href.includes("treatment-plans-account-receivable") ? "account-receivable" : ""}`}
            >
              {/* Note preview — flag + up to 2 lines of the manual note, in the
                  middle (freed phone/email space). Top-aligned, same height as
                  the tag box. Hover shows the full note. */}
              {showTagSelector && (
                <div
                  data-onboarding-id={tourId("row-note-cell")}
                  className={`treatment-card-note-cell ${
                    item.manual_attention_note ? "" : "treatment-card-note-cell--empty"
                  }`}
                >
                  {item.manual_attention_note ? (
                    <Tooltip title={item.manual_attention_note} placement="top">
                      <div className="treatment-card-note-inner">
                        <FlagFilled
                          aria-label="Manual attention note"
                          className="treatment-card-note-flag"
                          style={{
                            // Red while the patient carries the missing-info tag, neutral
                            // purple otherwise — purely visual; notes go with any tag.
                            color: (item.tags || []).includes(MISSING_INFO_TAG_NAME) ? "#c41d2a" : "#6b43b5",
                          }}
                        />
                        <div className="treatment-card-note-preview">{item.manual_attention_note}</div>
                      </div>
                    </Tooltip>
                  ) : (
                    <span className="treatment-card-note-empty-text">No notes available</span>
                  )}
                </div>
              )}

              {/* Status column — per-policy breakdown + estimate state (no
                  rollup). Up to 2 policies inline; 3+ collapse behind a "+N"
                  popover. Replaces both the per-policy chips and the old
                  Latest-Breakdown/Estimate dates. */}
              {showTagSelector && (
                <PolicyStatusCell
                  policyStatuses={item.policy_statuses}
                  onOpenPolicy={openPolicyInModal}
                  dataOnboardingId={tourId("row-activity-cell")}
                />
              )}

              {/* Days to Appointment - Conditional */}
              {showDaysColumn && (
                <div>
                  <PatientLeftDaysTime daysLeft={data.days_until_appointment} />
                </div>
              )}

              {isARSegment && (
                <div className="account-receivable-data">
                  <div style={{ color: "#848696" }}>${data.total_amount}</div>
                  <div style={{ color: "#848696" }}>${data.amount_between_0_30}</div>
                  <div style={{ color: "#848696" }}>${data.amount_between_31_60}</div>
                  <div style={{ color: "#848696" }}>${data.amount_between_61_90}</div>
                  <div style={{ color: "#848696" }}>${data.amount_greater_than_90}</div>
                </div>
              )}

              {showTagSelector && (
                <div
                  data-onboarding-id={tourId("row-tag-select")}
                  className={`treatment-card-tags-cell ${showDaysColumn ? "second-col-alignment" : ""}`}
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <TreatmentCardScrapeJobIndicator
                    patientId={item.id}
                    patientName={`${item.first_name} ${item.last_name}`}
                  />
                  <Popover
                    // Anchored to the tag Select so the note prompt
                    // appears right under the dropdown it belongs to.
                    // `trigger={[]}` keeps it fully controlled — open
                    // state is driven by `tagNotePopoverMode`.
                    open={tagNotePopoverMode !== null}
                    trigger={[]}
                    placement="bottomRight"
                    destroyTooltipOnHide
                    overlayStyle={{ maxWidth: 360 }}
                    content={
                      tagNotePopoverMode ? (
                        <TagNotePopover
                          mode={tagNotePopoverMode}
                          initialNote={item.manual_attention_note || ""}
                          onCancel={
                            tagNotePopoverMode === "add"
                              ? handleAddNoteCancel
                              : handleRemoveNoteCancel
                          }
                          onConfirm={
                            tagNotePopoverMode === "add"
                              ? handleAddNoteConfirm
                              : handleRemoveNoteConfirm
                          }
                        />
                      ) : null
                    }
                  >
                  <Select
                    placeholder="No custom tags"
                    options={tagDropdownOptions}
                    onClick={(e) => e.stopPropagation()}
                    style={{ flex: 1, minWidth: 0 }}
                    className="treatment-card-tag-select"
                    open={tagDropdownOpen}
                    onOpenChange={setTagDropdownOpen}
                    mode="multiple"
                    disabled={isSettingTagForPatient}
                    loading={isSettingTagForPatient}
                    // Custom tags only; system tags are merged back in on save.
                    value={customSelectedTagIds}
                    onChange={handleCustomTagsChange}
                    // Color the selected chips on the patient row using
                    // the per-tag-name palette in `lib/tagColors.js`.
                    // We apply the palette triple as inline styles rather
                    // than letting <Tag color={hex}> render as a solid
                    // saturated chip — that's the soft-chip aesthetic
                    // (light fill, colored border, colored text) that the
                    // top filter bar uses. Custom tags get the AntD preset
                    // (or AntD default) via colorForTag.
                    tagRender={(props) => {
                      const { label, closable, onClose } = props;
                      const stop = (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      };
                      const palette = paletteForTag(label);
                      return (
                        <Tag
                          color={palette ? undefined : colorForTag(label)}
                          onMouseDown={stop}
                          closable={closable}
                          onClose={onClose}
                          style={{
                            marginInlineEnd: 4,
                            ...(palette && {
                              background: palette.backgroundColor,
                              borderColor: palette.borderColor,
                              color: palette.color,
                            }),
                          }}
                        >
                          {label}
                        </Tag>
                      );
                    }}
                    // Color the items inside the dropdown list too, so
                    // the picker shows the same visual language as the
                    // selected chips.
                    optionRender={(option) => {
                      const palette = paletteForTag(option.label);
                      return (
                        <Tag
                          color={palette ? undefined : colorForTag(option.label)}
                          style={{
                            marginInlineEnd: 0,
                            ...(palette && {
                              background: palette.backgroundColor,
                              borderColor: palette.borderColor,
                              color: palette.color,
                            }),
                          }}
                        >
                          {option.label}
                        </Tag>
                      );
                    }}
                  />
                  </Popover>
                </div>
              )}
            </div>

            {/* Insurance actions + view — pinned to the right on wide layout */}
            {!showViewButton && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                {!showTagSelector && (
                  <TreatmentCardScrapeJobIndicator
                    patientId={item.id}
                    patientName={`${item.first_name} ${item.last_name}`}
                  />
                )}
                <TreatmentPlanListRowInsuranceActions patient={item} isTourDemo={isTourDemo} selectedSegment={selectedSegment} onPatientInsuranceUpdated={onPatientInsuranceUpdated} deepLinkRequest={deepLink} />
                {showTagSelector && !isFetchedItemSegment && (() => {
                  // The popover is shown whenever there's any pending action
                  // the user could still take: Instant Check (AI), Dispatch
                  // Request (regular tag), or Urgent (urgent tag). It's only
                  // fully disabled once both the regular AND urgent tags are
                  // already on the patient and there's no AI fetch in flight.
                  const everythingTagged = hasBreakdownRequested && hasBreakdownRequestedUrgent;
                  const anyActionInFlight = isRequestingBreakdown || isRequestingBreakdownUrgent || isAIFetching;

                  const breakdownButton = (
                    <Button
                      data-onboarding-id={tourId("row-request-breakdown-btn")}
                      className="details-button-color treatment-plan-row-insurance-action-button treatment-plan-row-breakdown-button"
                      onClick={
                        !everythingTagged && !anyActionInFlight
                          ? (e) => { e.stopPropagation(); setPopoverOpen(true); }
                          : undefined
                      }
                      loading={anyActionInFlight}
                      disabled={everythingTagged || anyActionInFlight}
                    >
                      <span className="treatment-plan-row-breakdown-label">
                        Request<br />Breakdown
                      </span>
                    </Button>
                  );

                  // Both tags already set: nothing left to do — show a
                  // disabled button with an explanatory tooltip.
                  if (everythingTagged) {
                    return (
                      <Tooltip title="Breakdown already requested (urgent).">
                        {breakdownButton}
                      </Tooltip>
                    );
                  }

                  return (
                    <Popover
                      open={popoverOpen}
                      onOpenChange={setPopoverOpen}
                      trigger="click"
                      placement="bottom"
                      destroyTooltipOnHide
                      content={
                        <div
                          style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 260 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {aiBreakdownAvailable && (
                            <Tooltip title="Open the request flow for this patient&apos;s breakdown." placement="left">
                              <Button
                                type="primary"
                                onClick={(e) => openRequestComposer(e, "breakdown", setPopoverOpen)}
                              >
                                Request Breakdown
                              </Button>
                            </Tooltip>
                          )}
                          <div style={{ display: "flex", gap: 0 }}>
                            <Tooltip title="Our team will grab the breakdown information for you as soon as possible." placement="left">
                              <Button
                                style={{ flex: 1, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                                onClick={handleRequestBreakdown}
                                loading={isRequestingBreakdown}
                                disabled={hasBreakdownRequested}
                              >
                                Normal Request
                              </Button>
                            </Tooltip>
                            <Tooltip title="Mark as urgent so our team prioritizes this breakdown." placement="left">
                              <Button
                                className="urgent-half-button"
                                style={{ flex: 1, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, marginLeft: -1 }}
                                onClick={handleRequestBreakdownUrgent}
                                loading={isRequestingBreakdownUrgent}
                                disabled={hasBreakdownRequestedUrgent}
                              >
                                Urgent Request
                              </Button>
                            </Tooltip>
                          </div>
                        </div>
                      }
                    >
                      <Tooltip title="Dispatch a request to have Breakdown fetched.">
                        {breakdownButton}
                      </Tooltip>
                    </Popover>
                  );
                })()}
                {showTagSelector && !isFetchedItemSegment && (() => {
                  // Estimate (predetermination) sibling of the breakdown popover:
                  // Instant Estimate (AI fetch) + Dispatch Request + Urgent. Same
                  // 98×78 footprint (shared classes). Disabled once both estimate
                  // request tags are set and no fetch is in flight.
                  const everythingEstimateTagged = hasEstimateRequested && hasEstimateRequestedUrgent;
                  const anyEstimateActionInFlight = isRequestingEstimate || isRequestingEstimateUrgent || isEstimateFetching;

                  const fetchEstimateButton = (
                    <Button
                      className="details-button-color treatment-plan-row-insurance-action-button treatment-plan-row-breakdown-button"
                      onClick={
                        !everythingEstimateTagged && !anyEstimateActionInFlight
                          ? (e) => { e.stopPropagation(); setEstimatePopoverOpen(true); }
                          : undefined
                      }
                      loading={anyEstimateActionInFlight}
                      disabled={everythingEstimateTagged || anyEstimateActionInFlight}
                    >
                      <span className="treatment-plan-row-breakdown-label">
                        Fetch<br />Estimate
                      </span>
                    </Button>
                  );

                  if (everythingEstimateTagged) {
                    return (
                      <Tooltip title="Estimate already requested (urgent).">
                        {fetchEstimateButton}
                      </Tooltip>
                    );
                  }

                  return (
                    <Popover
                      open={estimatePopoverOpen}
                      onOpenChange={setEstimatePopoverOpen}
                      trigger="click"
                      placement="bottom"
                      destroyTooltipOnHide
                      content={
                        <div
                          style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 260 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {aiEstimateAvailable && (
                            <Tooltip title="Open the request flow for this patient&apos;s estimate." placement="left">
                              <Button
                                type="primary"
                                onClick={(e) => openRequestComposer(e, "estimate", setEstimatePopoverOpen)}
                              >
                                Request Estimate
                              </Button>
                            </Tooltip>
                          )}
                          <div style={{ display: "flex", gap: 0 }}>
                            <Tooltip title="Our team will grab the estimate information for you as soon as possible." placement="left">
                              <Button
                                style={{ flex: 1, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                                onClick={handleRequestEstimate}
                                loading={isRequestingEstimate}
                                disabled={hasEstimateRequested}
                              >
                                Normal Request
                              </Button>
                            </Tooltip>
                            <Tooltip title="Mark as urgent so our team prioritizes this estimate." placement="left">
                              <Button
                                className="urgent-half-button"
                                style={{ flex: 1, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, marginLeft: -1 }}
                                onClick={handleRequestEstimateUrgent}
                                loading={isRequestingEstimateUrgent}
                                disabled={hasEstimateRequestedUrgent}
                              >
                                Urgent Request
                              </Button>
                            </Tooltip>
                          </div>
                        </div>
                      }
                    >
                      <Tooltip title="Dispatch a request to have an Estimate fetched.">
                        {fetchEstimateButton}
                      </Tooltip>
                    </Popover>
                  );
                })()}
                <ViewButton handleCardClick={handleCardClick} />
              </div>
            )}
          </div>
        </Card>
      </Col>
    </>
  );
};

function PatientLeftDaysTime({ daysLeft }) {
  const getStyle = () => {
    if (daysLeft >= 7) return { backgroundColor: "rgba(4, 201, 0, 0.1)", color: "#04C900" };
    if (daysLeft > 3) return { backgroundColor: "rgba(236, 148, 44, 0.15)", color: "#EC942C" };
    return { backgroundColor: "rgba(255, 0, 0, 0.08)", color: "#FF0000" };
  };

  const style = getStyle();

  return (
    <div
      style={{
        ...style,
        padding: "6px 16px",
        borderRadius: "100px",
        textAlign: "center",
        fontWeight: 500,
        whiteSpace: "nowrap",
        maxWidth: "max-content",
      }}
    >
      {daysLeft < 0
        ? `${Math.abs(daysLeft)} ${Math.abs(daysLeft) === 1 ? "Day" : "Days"} Passed`
        : `${daysLeft} ${daysLeft === 1 ? "Day" : "Days"} to Appointment`}
    </div>
  );
}

function ViewButton({ handleCardClick }) {
  return (
    <div className="view-button-box">
      <Tooltip title="View treatment plan">
        <Button
          type="text"
          className="details-button-color"
          onClick={(e) => {
            e.stopPropagation();
            handleCardClick(e);
          }}
          style={{ display: "flex", alignItems: "center", fontSize: 16, paddingInline: 4 }}
        >
          <img src={arrow} alt="View treatment plan details" />
        </Button>
      </Tooltip>
    </div>
  );
}

export default TreatmentCard;
